import { useComplianceChecker, useSocialCoachSession } from "@/utils";
import { useEffect, useMemo, useState } from "react";
import { CoachService } from "@/services";
import { toast } from "sonner";
import { AIService } from "@/services/AIService";
import React from "react";
import { Lightbulb, Quote, RefreshCw, WandSparkles } from "lucide-react";
import { Button, Card, Badge, Textarea } from "@/components/ui";
import { DetectedPhraseAlert } from "@/components/DetectedPhraseAlert";

const EXAMPLES = [
  "Why 2-1 buydowns are great for first-time buyers",
  "Using a tax return for a house down payment",
  "The benefits of relaxing on your own porch",
  "A funny loan pun to brighten the day",
  "Explain market volatility simply",
];

interface Props {
  handleBlockedWords?: (words: string[]) => void;
  handleFlaggedWords?: (words: string[]) => void;
  handlePrediction: (caption: string | null) => void;
  context?: string;
  contextType?: string;

  industryRoleOverride?: string;
  coachIdOverride?: string;
  playerIdOverride?: string;
}

export function CaptionCoach({
  handleBlockedWords,
  handleFlaggedWords,
  handlePrediction,
  context,
  contextType,
  industryRoleOverride,
  coachIdOverride,
  playerIdOverride,
}: Props) {
  const { coachId, playerId } = useSocialCoachSession();
  const effectiveCoachId = coachIdOverride || coachId;
  const effectivePlayerId = playerIdOverride || playerId;
  const { findBlockedWords, findFlaggedWords } = useComplianceChecker({
    coachId: effectiveCoachId,
    playerId: effectivePlayerId,
  });

  const [input, setInput] = useState("");
  const [prediction, setPrediction] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [industryRole, setIndustryRole] = useState<string | undefined>(industryRoleOverride);
  const [blockedWords, setBlockedWords] = useState<string[]>([]);
  const [flaggedWords, setFlaggedWords] = useState<string[]>([]);
  const [loadingMessage, setLoadingMessage] = useState<string>("Analyzing your instructions...");
  const showInstructions = !prediction && !loading;

  useEffect(() => {
    if (effectiveCoachId && effectivePlayerId && !industryRoleOverride) {
      CoachService.playerIndustryRoles(Number(effectiveCoachId), Number(effectivePlayerId))
        .then(res => setIndustryRole(res.industryRoles?.[0]?.name))
        .catch(console.error);
    }
  }, [coachId, playerId]);

  useEffect(() => {
    const blocked = findBlockedWords(prediction || "");
    setBlockedWords(blocked);
    setFlaggedWords(findFlaggedWords(prediction || ""));
  }, [prediction, findBlockedWords, findFlaggedWords]);

  useEffect(() => {
    handlePrediction(prediction);
  }, [prediction]);

  useEffect(() => {
    handleBlockedWords?.(blockedWords);
  }, [blockedWords]);

  useEffect(() => {
    handleFlaggedWords?.(flaggedWords);
  }, [flaggedWords]);

  useEffect(() => {
    if (loading) {
      const messages = [
        "Creating something just for you...",
        "Drafting the perfect caption...",
        "Optimizing for engagement...",
        "Almost there...",
      ];
      let i = 0;
      const interval = setInterval(() => {
        if (i <= messages.length) {
          setLoadingMessage(messages[i % messages.length]);
        }
        i++;
      }, 2000);
      return () => clearInterval(interval);
    } else {
      return undefined;
    }
  }, [loading, industryRole]);

  const handleGenerate = async (overrideInput?: string) => {
    const finalInput = overrideInput || input;
    if (!finalInput.trim()) {
      toast.error("Please enter some instructions or select an idea.");
      return;
    }

    setLoading(true);
    setPrediction(null);
    try {
      const response = await AIService.getCaption({
        input: finalInput,
        industryRole: industryRole,
      });

      if (response.success) {
        setPrediction(response.caption || "");
      } else {
        toast.error("Failed to generate caption. Please try again.");
      }
    } catch (e) {
      toast.error("An error occurred while generating the caption.");
    } finally {
      setLoading(false);
    }
  };

  const magicInstructions = useMemo(() => {
    if (context && context.length >= 50) {
      return `Generate a caption for this ${contextType || "context"}: """${context}"""`;
    } else {
      return undefined;
    }
  }, [context, contextType]);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
        {loading && (
          <div className="col-span-full py-20 flex flex-col items-center justify-center animate-in fade-in zoom-in-95 duration-500">
            <div className="relative mb-10">
              <div className="absolute inset-0 scale-150 bg-primary/10 rounded-full blur-3xl animate-pulse" />
              <div className="relative z-10 flex items-center justify-center size-24 bg-white rounded-3xl shadow-xl border border-primary/10">
                <WandSparkles className="size-12 text-primary animate-pulse" />
              </div>
            </div>

            <div className="flex flex-col items-center gap-2 mb-12">
              <img
                alt={"SocialCoach AI"}
                src="https://cdn.socialcoach.io/socoai_color_positive.svg"
                className="h-[25px]"
              />
              <div className="text-xl font-bold text-slate-800 tracking-tight">{loadingMessage}</div>
              <div className="text-slate-400 text-sm font-medium">Generating magic based on your instructions</div>
            </div>
          </div>
        )}

        {showInstructions && (
          <div className="flex flex-col gap-4 h-[400px]">
            <Card className="p-4 bg-slate-50 border-none shadow-none animate-in fade-in duration-300 flex flex-col h-full w-full">
              <div className="flex flex-col gap-3 h-full">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Your Instructions</label>
                <Textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Ex: Write a professional post about the benefits of refinancing..."
                  className="flex-1 bg-white border-slate-200 resize-none"
                />
                <div className="flex justify-end pt-1">
                  <Button onClick={() => handleGenerate()} disabled={loading || !input.trim()}>
                    <WandSparkles className="size-4" />
                    Generate Caption
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {prediction && !loading && (
          <div className="flex flex-col gap-4 col-span-full">
            <DetectedPhraseAlert blockedWords={blockedWords} flaggedWords={flaggedWords} />
            <Card className="p-6 border-primary/20 bg-primary/2 shadow-sm flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500 w-full">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-primary font-bold text-sm">
                  <Quote className="size-4" />
                  Generated Result
                </div>
                <Badge variant="outline" className="bg-white text-xs uppercase font-bold tracking-tight">
                  AI Generated
                </Badge>
              </div>
              <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{prediction}</div>

              <div className="flex justify-start items-center pt-2">
                <Button
                  variant={blockedWords.length > 0 ? "default" : "outline"}
                  onClick={() => {
                    setPrediction(null);
                    const newInstructions = "Do not use any of the blocked words: " + blockedWords.join(", ") + "\n";
                    if (!input && magicInstructions) {
                      setInput(magicInstructions + "\n" + newInstructions);
                    } else if (input) {
                      setInput(input + "\n" + newInstructions);
                    } else {
                      setInput(newInstructions);
                    }
                  }}
                >
                  <RefreshCw className="size-3" />
                  Try Different Instructions
                </Button>
              </div>
            </Card>
          </div>
        )}

        {showInstructions && (
          <div className="flex flex-col gap-4 bg-slate-50 rounded-2xl p-4 animate-in fade-in duration-300 h-[400px]">
            <div className="flex items-center gap-2 text-slate-900 font-bold text-sm shrink-0">
              <Lightbulb className="size-4 text-amber-500" />
              Try an idea
            </div>

            {magicInstructions && (
              <button
                onClick={() => {
                  handleGenerate(magicInstructions);
                }}
                className="group cursor-pointer relative flex flex-col gap-1 text-left p-3 font-sans rounded-xl border border-primary/20 bg-white hover:border-primary/40 transition-all duration-200 shadow-sm shrink-0"
              >
                <div className="flex items-center gap-2 text-primary font-bold text-sm">
                  <WandSparkles className="size-3.5" />
                  Magic Summarize
                </div>
                <div className="text-sm text-slate-500 font-medium leading-tight">
                  Automatically create a caption for your video.
                </div>
              </button>
            )}

            <div className="flex flex-col gap-2 overflow-y-auto pr-1 custom-scrollbar">
              {EXAMPLES.map((example, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setInput(example);
                    handleGenerate(example);
                  }}
                  className="text-left cursor-pointer font-sans text-sm text-slate-600 hover:text-primary hover:bg-white p-2 rounded-xl border border-transparent hover:border-primary/20 transition-all duration-200 bg-slate-100/50"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
